import { ActionButton } from "@fluentui/react";
import { type User } from "@sentry/types";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { SortDirection, UserSortKey, type Vehicle, VehicleSortKey } from "../../api";
import { defaultPageSize } from "../../utils/constants";

export interface FilterVehicleMovementPeriodListProps {
    /** This callback function allows the filter to rerender
     * the parent component. */
    setVehicleMovementPeriodListFilter(filter: FilterVehicleMovementPeriodListQuery): void;
}

export type FilterVehicleMovementPeriodListQuery = {
    filteredVehicles: string[];
    filteredFleetUsers: string[];
    isFiltered: boolean;
};

import { store } from "../../store";
import SearchableDropdown from "../atoms/searchable-dropdown";

@observer
export class FilterVehicleMovementPeriodList extends React.Component<FilterVehicleMovementPeriodListProps> {
    @observable private accessor filteredVehicles: string[] = [];
    @observable private accessor filteredFleetUsers: string[] = [];
    @observable private accessor isFiltered: boolean = false;

    constructor(props: FilterVehicleMovementPeriodListProps) {
        super(props);
        makeObservable(this);
    }

    private setVehicleFilter(filter: string): void {
        // Remove element if it is already clicked.
        if (this.filteredVehicles.includes(filter)) {
            this.filteredVehicles = this.filteredVehicles.filter((id) => id !== filter);
            this.setVehicleMovementPeriodListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredVehicles = [filter, ...this.filteredVehicles];
        this.setVehicleMovementPeriodListFilter();
    }

    private setFleetUserFilter(filter: string): void {
        // Remove element if it is already clicked.
        if (this.filteredFleetUsers.includes(filter)) {
            this.filteredFleetUsers = this.filteredFleetUsers.filter((id) => id !== filter);
            this.setVehicleMovementPeriodListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredFleetUsers = [filter, ...this.filteredFleetUsers];
        this.setVehicleMovementPeriodListFilter();
    }

    @action.bound private clearFilter(): void {
        this.filteredVehicles = [];
        this.filteredFleetUsers = [];
        this.isFiltered = false;
        this.setVehicleMovementPeriodListFilter();
    }

    private setVehicleMovementPeriodListFilter(): void {
        const filterQuery: FilterVehicleMovementPeriodListQuery = {
            filteredVehicles: this.filteredVehicles,
            filteredFleetUsers: this.filteredFleetUsers,
            isFiltered: this.isFiltered,
        };
        this.props.setVehicleMovementPeriodListFilter(filterQuery);
    }

    public render(): JSX.Element {
        return (
            <>
                <SearchableDropdown
                    query={{
                        pageSize: defaultPageSize,
                        sortKey: VehicleSortKey.SERIAL_NUMBER,
                        sortDirection: SortDirection.ASCENDING,
                    }}
                    placeholder={store.i18n.t("schema.vehicle.vehicle")}
                    repository={store.repositoryVehicles}
                    formatEntity={(vehicle: Vehicle) => vehicle.serialNumber}
                    selectedKeys={this.filteredVehicles}
                    onChange={(vehicleId: string | undefined) => {
                        if (vehicleId === undefined) {
                            return;
                        }
                        this.setVehicleFilter(vehicleId);
                    }}
                    multiSelect
                />

                <SearchableDropdown
                    query={{
                        pageSize: defaultPageSize,
                        sortKey: UserSortKey.EMPLOYEE_ID,
                        sortDirection: SortDirection.ASCENDING,
                    }}
                    placeholder={store.i18n.t("schema.user.operator")}
                    repository={store.repositoryUsers}
                    formatEntity={(user: User) => user.employeeId}
                    selectedKeys={this.filteredFleetUsers}
                    onChange={(userId: string | undefined) => {
                        if (userId === undefined) {
                            return;
                        }
                        this.setFleetUserFilter(userId);
                    }}
                    multiSelect
                />

                <ActionButton
                    iconProps={{ iconName: "clearFilter" }}
                    allowDisabledFocus
                    disabled={!this.isFiltered}
                    checked={true}
                    onClick={this.clearFilter}
                ></ActionButton>
            </>
        );
    }
}
